import type { ArtifactPickedPart, ArtifactPickKind } from "@entrilia/shared/artifactRuntime/protocol";
import type { SmartViewArtifactVersion } from "../../../../../api/types/smartViewArtifactsTypes";
import { findSourceTitle, findViewTitle } from "../canvas/checkResult";

export interface PickedPart {
  kind: ArtifactPickKind;
  label: string;
  componentLocation: string;
  line: string;
}

export const MAX_PICKED_PARTS = 5;

const MAX_LABEL_LENGTH = 120;
const MAX_NAME_LENGTH = 200;
const MAX_COMPONENTS = 3;
const MAX_SOURCES = 5;
const MAX_COLUMNS = 30;
const MAX_SERIES = 10;
const MAX_LINE_LENGTH = 1_000;

const collapseWhitespace = (text: string): string => text.replace(/\s+/g, " ").trim();

const describeViewPlace = (version: SmartViewArtifactVersion, view: string): string => {
  const key = collapseWhitespace(view).slice(0, MAX_NAME_LENGTH);
  return key === "" ? "the main view" : `the "${findViewTitle(version, key) || key}" view (${key})`;
};

const describeSeries = ({ series }: ArtifactPickedPart): string => {
  const shown = series.slice(0, MAX_SERIES);
  const types = [...new Set(shown.map(({ type }) => collapseWhitespace(type).slice(0, MAX_NAME_LENGTH)))].join(", ");
  const names = shown.map(({ name }) => collapseWhitespace(name).slice(0, MAX_NAME_LENGTH)).join(", ");
  const details = [types, names && `series ${names}`].filter(Boolean).join("; ");
  return details && ` (${details})`;
};

const describeCode = (componentLocations: string[]): string => {
  const [innermost, ...outer] = componentLocations;
  if (!innermost) {
    return "";
  }
  return `Code: ${innermost}${outer.length > 0 ? `, inside ${outer.join(", inside ")}` : ""}.`;
};

export const toPickedPart = (version: SmartViewArtifactVersion, part: ArtifactPickedPart): PickedPart => {
  const label = collapseWhitespace(part.label).slice(0, MAX_LABEL_LENGTH);
  const componentLocations = part.components
    .slice(0, MAX_COMPONENTS)
    .map(
      ({ name, path }) =>
        `${collapseWhitespace(name).slice(0, MAX_NAME_LENGTH)} in ${collapseWhitespace(path).slice(0, MAX_NAME_LENGTH)}`
    );
  const [innermostComponent] = part.components;
  const componentLocation = innermostComponent
    ? `${collapseWhitespace(innermostComponent.name).slice(0, MAX_NAME_LENGTH)} in ${collapseWhitespace(
        innermostComponent.path
      )
        .replace(/\.tsx?$/, "")
        .slice(0, MAX_NAME_LENGTH)}`
    : "";
  const sources = part.sources.slice(0, MAX_SOURCES).map(({ key, columns }) => {
    const sourceKey = collapseWhitespace(key).slice(0, MAX_NAME_LENGTH);
    const title = findSourceTitle(version, sourceKey);
    const columnKeys = columns
      .slice(0, MAX_COLUMNS)
      .map((column) => collapseWhitespace(column).slice(0, MAX_NAME_LENGTH));
    const columnText = columnKeys.length > 0 ? `, columns ${columnKeys.join(", ")}` : "";
    return `source ${sourceKey}${title ? ` ("${title}")` : ""}${columnText}`;
  });
  const line = [
    `A ${part.kind}${label && ` "${label}"`}${describeSeries(part)} in ${describeViewPlace(version, part.view)}.`,
    describeCode(componentLocations),
    sources.length > 0 ? `Data: ${sources.join("; ")}.` : "",
  ]
    .filter(Boolean)
    .join(" ");
  return {
    kind: part.kind,
    label,
    componentLocation,
    line: line.slice(0, MAX_LINE_LENGTH),
  };
};

export const canAddPickedPart = (parts: PickedPart[], part: PickedPart): boolean =>
  parts.length < MAX_PICKED_PARTS && !parts.some(({ line }) => line === part.line);

export const addPickedPart = (parts: PickedPart[], part: PickedPart): PickedPart[] =>
  canAddPickedPart(parts, part) ? [...parts, part] : parts;
